<template>
    <v-col class="pb-offset pa-0">
        <v-container fluid class="pa-0">
            <crud
                title='Calendari'
                action-icon='alarm'
                model-def='calendars'
                action-position='absolute'
                :position-left="true"
                :position-right="false"
                direction="right"
            ></crud>


            <v-card class="round-card background-2 mt-4">
                <v-alert
                    v-if="hasDisconnectedAccount"
                    type="warning"
                    text
                    tile
                    dense
                    class="mb-0"
                >
                    <div class="font-weight-medium">Hai uno o più account Google scollegati</div>
                    <div class="caption">
                        Il token Google non è più valido (spesso dopo un cambio password o revoca accesso).
                        Clicca <v-icon small>mdi-google</v-icon> sull'account per riconnetterti con lo stesso indirizzo
                        e mantenere la sincronizzazione del calendario.
                    </div>
                </v-alert>
                <v-card-title class="pa-3">
				    <p class="pl-2 pl-xl-3">Account Google Collegati</p>
                </v-card-title>
				<v-divider></v-divider>
                <v-row>
                    <v-col>
                        <v-list two-line class="background-2">
                            <template v-for="(account, a) in googleAccounts">
                                <v-list-item :key="a">
                                    <v-list-item-avatar class="v-avatar-show">
                                        <v-avatar v-if="account.disconnected_at" :size="26" color="warning">
                                            <v-icon dark small>mdi-calendar-remove</v-icon>
                                        </v-avatar>
                                        <v-avatar v-else-if="account.avatar"
                                            :size="26"
                                            class="custom-avatar"
                                            v-ripple
                                            :style="{'background-image': 'url(' + account.avatar + ')'}">
                                        </v-avatar>
                                        <v-avatar v-else :class="$eventBusUtility.bgColor(account.user_id)" :size="26">
                                            <span class="white--text fs14 capitalize" v-if="account.name">{{ account.name.charAt(0) }}</span>
                                        </v-avatar>
                                    </v-list-item-avatar>
                                    <v-list-item-content>
                                        <v-list-item-title>
                                            Email
                                            <v-chip v-if="account.disconnected_at" x-small color="warning" class="ml-2">
                                                <v-icon left x-small>mdi-link-off</v-icon>
                                                Disconnesso
                                            </v-chip>
                                        </v-list-item-title>
                                        <v-list-item-subtitle>{{ account.name }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                    <v-list-item-action class="flex-row">
                                        <v-btn
                                            v-if="account.disconnected_at"
                                            icon
                                            color="warning"
                                            @click="reconnectAccount(account)"
                                            :loading="reconnectingAccountId === account.id"
                                            :disabled="disconnectingAccountId !== null"
                                        >
                                            <v-icon>mdi-google</v-icon>
                                        </v-btn>
                                        <v-btn
                                            icon
                                            color="error"
                                            @click="confirmDisconnect(account)"
                                            :loading="disconnectingAccountId === account.id"
                                            :disabled="(disconnectingAccountId !== null && disconnectingAccountId !== account.id) || reconnectingAccountId !== null"
                                        >
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-list-item-action>
                                </v-list-item>
                            </template>
                        </v-list>
                    </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="info" dark @click="addAccount()">Aggiungi Account</v-btn>
                </v-card-actions>
            </v-card>

            <v-card class="round-card background-2 mt-4">
                <v-card-title class="pa-3">
				    <p class="pl-2 pl-xl-3">Impostazioni</p>
                </v-card-title>
				<v-divider></v-divider>
                <v-subheader class="pl-6">Notifiche</v-subheader>
                <v-row class="ma-0 px-4">
                    <v-col cols="12" class="py-0">
                        <v-switch
                            inset
                            class="text-center"
                            label="Ricevi notifiche eventi in calendario un giorno esatto prima dell'evento"
                            v-model="settings['event_notifications_the_day_before']"
                            :input-value="[true,false]"
                            color="primary"
                        ></v-switch>
                    </v-col>
                    <v-col cols="12" class="py-0">
                        <v-switch
                            inset
                            class="text-center"
                            label="Ricevi notifiche eventi in calendario ad un numero personalizzato di ore e minuti prima dell'evento"
                            v-model="settings['event_notifications_time_before']"
                            :input-value="[true,false]"
                            color="primary"
                        ></v-switch>
                    </v-col>
                    <v-col lg="1" md="1" sm="2" cols="2" class="py-0">
                        <v-text-field v-if="settings['event_notifications_time_before']"
                            label="Ore"
                            v-model="settings['event_notifications_before_hours']"
                            outlined
                            type="number"
                            dense
                            :error-messages="errors.collect('Ore')"
                            v-validate="'required|max_value:24'"
                            data-vv-name="Ore"
                        ></v-text-field>
                    </v-col>
                    <v-col lg="1" md="1" sm="2" cols="2" class="py-0">
                        <v-text-field v-if="settings['event_notifications_time_before']"
                            label="Minuti"
                            v-model="settings['event_notifications_before_minutes']"
                            outlined
                            type="number"
                            dense
                            :error-messages="errors.collect('Minuti')"
                            v-validate="'required|max_value:59'"
                            data-vv-name="Minuti"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-divider class="my-2"></v-divider>
                <v-subheader class="pl-6">Eventi</v-subheader>
                <v-row class="ma-0 px-4">
                    <v-col cols="12" sm="4" md="3" lg="2" class="py-0">
                        <v-text-field
                            label="Durata default evento (minuti)"
                            v-model.number="settings['event_default_duration_minutes']"
                            outlined
                            type="number"
                            dense
                            :error-messages="errors.collect('Durata default')"
                            v-validate="'required|integer|min_value:5|max_value:1440'"
                            data-vv-name="Durata default"
                            hint="Predefinito: 60 minuti"
                            persistent-hint
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="primary" dark @click="saveSettings">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-container>

        <!-- Dialog di conferma disconnessione -->
        <v-dialog v-model="disconnectDialog" max-width="500">
            <v-card class="background-2 round-card">
                <v-card-title class="headline">
                    Conferma disconnessione
                </v-card-title>
                <v-card-text>
                    Sei sicuro di voler scollegare l'account Google <strong>{{ accountToDisconnect ? accountToDisconnect.name : '' }}</strong>?
                    <br><br>
                    Questa azione rimuoverà anche tutti i calendari sincronizzati da questo account.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        @click="disconnectDialog = false"
                    >
                        Annulla
                    </v-btn>
                    <v-btn
                        color="error"
                        text
                        @click="disconnectAccount"
                        :loading="disconnecting"
                    >
                        Scollega
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-col>

</template>

<script>
    import Vue from 'vue'

	export default {
        name:'profile-calendar',
		data() {
			return {
                account: {},
                googleAccounts: {},
                disconnectDialog: false,
                accountToDisconnect: null,
                disconnecting: false,
                disconnectingAccountId: null,
                reconnectingAccountId: null,
                settings: {
                    'event_notifications_the_day_before': false,
                    'event_notifications_time_before': false,
                    'event_notifications_before_hours': '',
                    'event_notifications_before_minutes': '',
                    'event_default_duration_minutes': 60,
                }
			}
        },

        computed: {
            hasDisconnectedAccount() {
                return Array.isArray(this.googleAccounts)
                    && this.googleAccounts.some(a => a.disconnected_at)
            }
        },

        watch: {
            settings: {
                handler() {
                    if(!this.settings['event_notifications_time_before']) {
                        Vue.set(this.settings, 'event_notifications_before_hours', null)
                        Vue.set(this.settings, 'event_notifications_before_minutes', null)
                    }
                },
                deep: true,
            }
        },
        
        methods: {

            addAccount() {
                this.axios.get('google/oauth', { params: { spa_origin: window.location.origin } }).then(response => {
                    // get body data
                    this.account = response.data.model
                    window.open(response.data.url) //'name', 'height=600,width=450')

                }, response => {
                        // error callback
                })
            },

            confirmDisconnect(account) {
                this.accountToDisconnect = account
                this.disconnectDialog = true
            },

            disconnectAccount() {
                if (!this.accountToDisconnect) return

                this.disconnecting = true
                this.disconnectingAccountId = this.accountToDisconnect.id

                this.axios.delete(`google/${this.accountToDisconnect.id}`).then(response => {
                    this.$store.commit('setSnackbar', {
                        timeout: 6000,
                        text: 'Account Google scollegato con successo',
                        value: true,
                        color: 'success'
                    })

                    // Ricarica la lista degli account
                    this.loadGoogleAccounts()

                    this.disconnectDialog = false
                    this.accountToDisconnect = null
                    this.disconnecting = false
                    this.disconnectingAccountId = null
                }).catch(error => {
                    this.$store.commit('setSnackbar', {
                        timeout: 6000,
                        text: 'Errore durante la disconnessione dell\'account',
                        value: true,
                        color: 'error'
                    })

                    this.disconnecting = false
                    this.disconnectingAccountId = null
                })
            },

            loadGoogleAccounts() {
                this.axios.post('google-filters').then(response => {
                    this.googleAccounts = response.data.data
                }, response => {
                    // error callback
                })
            },

            reconnectAccount(account) {
                this.reconnectingAccountId = account.id
                const params = { login_hint: account.name, spa_origin: window.location.origin }
                this.axios.get('google/oauth', { params })
                    .then(response => {
                        if (response.data.url) {
                            window.location.href = response.data.url
                        }
                    })
                    .catch(() => {
                        this.$store.commit('setSnackbar', {
                            timeout: 6000,
                            text: 'Errore durante la riconnessione',
                            value: true,
                            color: 'error'
                        })
                    })
                    .finally(() => {
                        this.reconnectingAccountId = null
                    })
            },

            loadCalendarSettings() {
                this.axios.get('profile-calendar-settings').then(response => {
                    if (response.data.settings) {
                        Object.keys(response.data.settings).forEach(key => {
                            if (response.data.settings[key] !== null) {
                                this.$set(this.settings, key, response.data.settings[key])
                            }
                        })
                    }
                })
            },

            saveSettings() {
                this.$validator.validateAll().then(result => {
                    if (!result) return

                    this.axios.post('profile-calendar-settings', this.settings).then(() => {
                        this.$store.commit('setSnackbar', {
                            timeout: 6000,
                            text: 'Impostazioni salvate',
                            value: true,
                            color: 'success'
                        })
                    })
                })
            }
        },

        beforeCreate() {
            if(this.$route.query.code) {
                this.axios.get('google/oauth?code='+this.$route.query.code).then(response => {
                    this.$store.commit('setSnackbar', {timeout: 6000, text: 'Account Google collegato con successo', value: true, color: 'success'})
                    // Ricarica gli account dopo l'autenticazione
                    if (this.loadGoogleAccounts) {
                        this.loadGoogleAccounts()
                    }
                }, response => {
                    this.$store.commit('setSnackbar', {timeout: 6000, text: 'Collegamento account Google non riuscito', value: true, color: 'error'})
                })
            }
        },

		created() {
            this.loadGoogleAccounts()
            this.loadCalendarSettings()
            this.$store.commit('endProgress')
		}
	}
</script>

<style>

</style>